import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getContacts,
  createContact,
  updateContact,
  deleteContact,
  getContactMessages,
  getInboxMessages,
  type Contact,
  type ContactInput,
  type ContactKind,
  type HandleKind,
  type Message,
  type InboxMessage,
} from '../api.js';
import {
  FILTERS,
  KIND_LABEL,
  attachmentLabel,
  cleanContactInput,
  contactFromHandle,
  displayHandle,
  emptyContact,
  filterContacts,
  fmtMessageTime,
  primaryHandle,
  toContactInput,
  type ContactFilter,
} from '../lib/contacts.js';
import { Button, Chip, EmptyState, Avatar, Group, Icon, IconButton, Page, Row, Segmented, Sheet, useToast } from '../ui/index.js';

/**
 * Registry of who is who for the message dispatcher: which handles belong to
 * which client, where their site lives, and whether the agent may act on
 * their texts unprompted. Left: searchable list. Right: the editable record
 * plus its recent messages. The Inbox view lists messages from handles no
 * contact claims yet, with a one-tap path to add them.
 */

type Selection = number | 'new' | null;
type View = 'contacts' | 'inbox';
const KINDS: ContactKind[] = ['client', 'person', 'vendor'];

export default function Contacts(): JSX.Element {
  const [contacts, setContacts] = useState<Contact[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const toastApi = useToast();
  const setToast = (m: string): void => toastApi.info(m);
  const [view, setView] = useState<View>('contacts');
  const [filter, setFilter] = useState<ContactFilter>('all');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Selection>(null);
  const [draft, setDraft] = useState<ContactInput | null>(null);
  const [baseline, setBaseline] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoadError(null);
    getContacts()
      .then(setContacts)
      .catch((err: unknown) => setLoadError(err instanceof Error ? err.message : 'Failed to load contacts.'));
  }, []);
  useEffect(load, [load]);

  const dirty = draft !== null && JSON.stringify(draft) !== baseline;
  const visible = useMemo(() => filterContacts(contacts ?? [], filter, query), [contacts, filter, query]);

  function confirmDiscard(): boolean {
    return !dirty || window.confirm('Discard unsaved changes?');
  }

  function open(c: Contact): void {
    if (selected === c.id) return;
    if (!confirmDiscard()) return;
    const input = toContactInput(c);
    setSelected(c.id);
    setDraft(input);
    setBaseline(JSON.stringify(input));
    setFormError(null);
  }

  function openNew(prefill?: ContactInput): void {
    if (!confirmDiscard()) return;
    const input = prefill ?? emptyContact('client');
    setView('contacts');
    setSelected('new');
    setDraft(input);
    // A prefilled form counts as unsaved work, so the baseline is the blank form.
    setBaseline(JSON.stringify(emptyContact(input.kind)));
    setFormError(null);
  }

  function close(): void {
    if (!confirmDiscard()) return;
    setSelected(null);
    setDraft(null);
    setBaseline('');
    setFormError(null);
  }

  async function save(): Promise<void> {
    if (!draft) return;
    const body = cleanContactInput(draft);
    if (!body.name) {
      setFormError('Name is required.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      let saved: Contact;
      if (selected === 'new') {
        saved = await createContact(body);
        setContacts((prev) => [...(prev ?? []), saved]);
        setToast(`Added ${saved.name}.`);
      } else if (typeof selected === 'number') {
        saved = await updateContact(selected, body);
        setContacts((prev) => (prev ?? []).map((c) => (c.id === saved.id ? saved : c)));
        setToast('Saved.');
      } else {
        return;
      }
      const input = toContactInput(saved);
      setSelected(saved.id);
      setDraft(input);
      setBaseline(JSON.stringify(input));
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  }

  async function remove(): Promise<void> {
    if (typeof selected !== 'number') return;
    const c = contacts?.find((x) => x.id === selected);
    if (!window.confirm(`Delete "${c?.name ?? 'this contact'}"? Their handles will no longer be recognised.`)) return;
    setSaving(true);
    setFormError(null);
    try {
      await deleteContact(selected);
      setContacts((prev) => (prev ?? []).filter((x) => x.id !== selected));
      setSelected(null);
      setDraft(null);
      setBaseline('');
      setToast('Deleted.');
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Could not delete.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Page
      title="Contacts"
      sub="Who the dispatcher listens to"
      className="bc-contacts"
      actions={<IconButton icon="plus" label="New contact" onClick={() => openNew()} />}
    >
      <Segmented
        label="View"
        value={view}
        options={[
          { value: 'contacts', label: 'Contacts' },
          { value: 'inbox', label: 'Unknown senders' },
        ]}
        onChange={setView}
      />

      {loadError && (
        <p className="bc-banner" role="alert">
          <Icon name="alert" size={18} />
          {loadError}
          <button type="button" className="bc-banner__act" onClick={load}>
            Retry
          </button>
        </p>
      )}

      {view === 'inbox' ? (
        <Inbox contacts={contacts} onAdd={(handle, name) => openNew(contactFromHandle(handle, name))} />
      ) : (
        <>
          <div className="bc-search">
            <Icon name="search" size={18} />
            <input
              type="search"
              placeholder="Search name, project, number"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search contacts"
            />
          </div>
          <div className="ui-chips" role="group" aria-label="Kind">
            {FILTERS.map((f) => (
              <Chip key={f.key} on={filter === f.key} onClick={() => setFilter(f.key)}>
                {f.label}
              </Chip>
            ))}
          </div>
          {contacts === null && !loadError ? (
            <ul className="ui-group" aria-busy="true">
              {[0, 1, 2].map((i) => (
                <li key={i} className="ui-swipe">
                  <div className="ui-row">
                    <span className="skeleton bc-skel" />
                  </div>
                </li>
              ))}
            </ul>
          ) : visible.length === 0 ? (
            <EmptyState
              icon="contact"
              title={contacts?.length ? 'No matches' : 'No contacts yet'}
              hint={contacts?.length ? 'Try a different search or filter.' : 'Add one, or pick someone up from Unknown senders.'}
            />
          ) : (
            <Group>
              {visible.map((c) => (
                <ContactRow key={c.id} contact={c} onClick={() => open(c)} />
              ))}
            </Group>
          )}
        </>
      )}

      {draft !== null && (
        <Sheet
          title={selected === 'new' ? 'New contact' : draft.name || 'Contact'}
          onClose={close}
          action={{
            label: saving ? 'Saving…' : selected === 'new' ? 'Add' : 'Save',
            onClick: () => void save(),
            disabled: saving || (selected !== 'new' && !dirty),
          }}
          className="bc-csheet"
        >
          <ContactForm
            draft={draft}
            isNew={selected === 'new'}
            dirty={dirty}
            saving={saving}
            error={formError}
            onChange={setDraft}
            onSave={() => void save()}
            onDelete={() => void remove()}
          />
          {typeof selected === 'number' && <ContactMessages contactId={selected} />}
        </Sheet>
      )}
    </Page>
  );
}

// --- List row ----------------------------------------------------------------

const KIND_COLOR: Record<ContactKind, string> = { client: '#0a84ff', person: '#af52de', vendor: '#ff9500' };

/** The shared neutral avatar; the contact's kind shows as the small dot. */
function KindAvatar({ name, kind }: { name: string; kind: ContactKind }): JSX.Element {
  return <Avatar name={name} color={KIND_COLOR[kind]} />;
}

function ContactRow({ contact, onClick }: { contact: Contact; onClick: () => void }): JSX.Element {
  const handle = primaryHandle(contact);
  const meta = [contact.kind === 'client' ? contact.project : contact.contactName, handle ? displayHandle(handle.value, handle.kind) : null]
    .filter(Boolean)
    .join(' · ');
  return (
    <Row
      leading={<KindAvatar name={contact.name} kind={contact.kind} />}
      title={contact.name}
      sub={meta || undefined}
      detail={
        contact.kind === 'client' ? (
          <span className={`bc-auto${contact.auto ? ' bc-auto--on' : ''}`} title={contact.auto ? 'Acts on their texts and sends the diff' : 'Waits for "do <name>"'}>
            {contact.auto ? 'Auto' : 'Manual'}
          </span>
        ) : undefined
      }
      onClick={onClick}
      chevron
    />
  );
}

// --- Form --------------------------------------------------------------------

function ContactForm({
  draft,
  isNew,
  dirty,
  saving,
  error,
  onChange,
  onSave,
  onDelete,
}: {
  draft: ContactInput;
  isNew: boolean;
  dirty: boolean;
  saving: boolean;
  error: string | null;
  onChange: (next: ContactInput) => void;
  onSave: () => void;
  onDelete: () => void;
}): JSX.Element {
  const set = <K extends keyof ContactInput>(key: K, value: ContactInput[K]): void => onChange({ ...draft, [key]: value });
  const text = (key: 'contactName' | 'project' | 'domain' | 'staging' | 'docker' | 'notes') => ({
    value: draft[key] ?? '',
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => set(key, e.target.value),
  });

  function setHandle(i: number, patch: Partial<ContactInput['handles'][number]>): void {
    set(
      'handles',
      draft.handles.map((h, j) => (j === i ? { ...h, ...patch } : h)),
    );
  }
  function addHandle(kind: HandleKind): void {
    set('handles', [...draft.handles, { kind, value: '', label: null }]);
  }
  function removeHandle(i: number): void {
    set(
      'handles',
      draft.handles.filter((_, j) => j !== i),
    );
  }

  const isClient = draft.kind === 'client';
  const field = (label: string, input: JSX.Element): JSX.Element => (
    <li className="ui-swipe">
      <label className="bc-field">
        <span className="bc-field__label">{label}</span>
        {input}
      </label>
    </li>
  );

  return (
    <form
      className="bc-sheetform bc-cform"
      onSubmit={(e) => {
        e.preventDefault();
        onSave();
      }}
    >
      <div className="seg bc-seg-full" role="radiogroup" aria-label="Kind">
        {KINDS.map((k) => (
          <button
            key={k}
            type="button"
            role="radio"
            aria-checked={draft.kind === k}
            className={`seg__btn${draft.kind === k ? ' seg__btn--active' : ''}`}
            onClick={() => set('kind', k)}
          >
            {KIND_LABEL[k]}
          </button>
        ))}
      </div>

      <ul className="ui-group bc-inset">
        {field(
          isClient ? 'Business' : 'Name',
          <input
            className="bc-field__input"
            value={draft.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder={isClient ? "Ferguson's Garage Doors" : 'Full name'}
            autoFocus={isNew}
            required
          />,
        )}
        {field('Contact', <input className="bc-field__input" placeholder={isClient ? 'Who texts you' : 'Optional'} {...text('contactName')} />)}
      </ul>

      <section>
        <h3 className="bc-legend">Phone and email</h3>
        <ul className="ui-group bc-inset">
          {draft.handles.map((h, i) => (
            <li key={h.id ?? `new-${i}`} className="ui-swipe">
              <div className="bc-field bc-handle">
                <select
                  className="bc-handle__kind"
                  value={h.kind}
                  onChange={(e) => setHandle(i, { kind: e.target.value as HandleKind })}
                  aria-label="Handle type"
                >
                  <option value="phone">Phone</option>
                  <option value="email">Email</option>
                </select>
                <input
                  className="bc-field__input bc-handle__value"
                  type={h.kind === 'email' ? 'email' : 'tel'}
                  inputMode={h.kind === 'email' ? 'email' : 'tel'}
                  placeholder={h.kind === 'email' ? 'name@example.com' : '(540) 555-1234'}
                  value={h.value}
                  onChange={(e) => setHandle(i, { value: e.target.value })}
                  aria-label={`${h.kind} value`}
                />
                <input
                  className="bc-field__input bc-handle__label"
                  placeholder="Label"
                  value={h.label ?? ''}
                  onChange={(e) => setHandle(i, { label: e.target.value })}
                  aria-label="Handle label"
                />
                <button type="button" className="bc-handle__del" aria-label="Remove handle" onClick={() => removeHandle(i)}>
                  <Icon name="x" size={14} />
                </button>
              </div>
            </li>
          ))}
          <li className="ui-swipe">
            <button type="button" className="ui-more bc-addrow" onClick={() => addHandle('phone')}>
              <Icon name="plus" size={20} /> Add phone
            </button>
          </li>
          <li className="ui-swipe">
            <button type="button" className="ui-more bc-addrow" onClick={() => addHandle('email')}>
              <Icon name="plus" size={20} /> Add email
            </button>
          </li>
        </ul>
        {draft.handles.length === 0 && <p className="ui-group__foot">Incoming messages are matched on these.</p>}
      </section>

      {isClient && (
        <section>
          <h3 className="bc-legend">Site</h3>
          <ul className="ui-group bc-inset">
            {field('Project', <input className="bc-field__input" placeholder="fergusons-garage-doors" {...text('project')} />)}
            {field('Domain', <input className="bc-field__input" placeholder="fergusonsgaragedoor.com" {...text('domain')} />)}
            {field('Staging', <input className="bc-field__input" placeholder="ferg.vugdalic.net" {...text('staging')} />)}
            {field('Docker', <input className="bc-field__input" placeholder="compose service" {...text('docker')} />)}
            <li className="ui-swipe">
              <label className="bc-field">
                <span className="bc-field__text">
                  Auto
                  <span className="bc-field__hint">
                    Act on their texts and send me the diff. Off = tell me and wait for &ldquo;do {draft.name || '<name>'}&rdquo;.
                  </span>
                </span>
                <input type="checkbox" className="bc-switch" checked={draft.auto} onChange={(e) => set('auto', e.target.checked)} />
              </label>
            </li>
          </ul>
        </section>
      )}

      <ul className="ui-group bc-inset">
        <li className="ui-swipe">
          <label className="bc-field bc-field--stack">
            <span className="bc-field__label">Notes</span>
            <textarea className="bc-field__input" rows={3} placeholder="Anything the agent should know" {...text('notes')} />
          </label>
        </li>
      </ul>

      {error && (
        <p className="bc-formerr" role="alert">
          {error}
        </p>
      )}

      {(isNew || dirty) && (
        <Button type="submit" size="lg" block disabled={saving}>
          {saving ? 'Saving…' : isNew ? 'Add contact' : 'Save'}
        </Button>
      )}
      {!isNew && (
        <Button variant="destructive" block onClick={onDelete} disabled={saving}>
          Delete contact
        </Button>
      )}
    </form>
  );
}

// --- Messages ----------------------------------------------------------------

function MessageRow({ m, showHandle }: { m: Message; showHandle?: boolean }): JSX.Element {
  const them = m.direction === 'in';
  return (
    <div className={`bc-bubble bc-bubble--${them ? 'in' : 'out'}`}>
      {m.text && <p className="bc-bubble__text">{m.text}</p>}
      {m.attachments.length > 0 && (
        <div className="bc-bubble__files">
          {m.attachments.map((a, i) => (
            <span key={i} className="bc-bubble__file" title={a.local ?? a.path ?? a.mime}>
              <Icon name="file" size={14} /> {attachmentLabel(a)}
            </span>
          ))}
        </div>
      )}
      <span className="bc-bubble__meta">
        {them && showHandle ? `${displayHandle(m.handle)} · ` : ''}
        {m.channel} · {fmtMessageTime(m.sentAt)}
      </span>
    </div>
  );
}

function ContactMessages({ contactId }: { contactId: number }): JSX.Element {
  const [messages, setMessages] = useState<Message[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setMessages(null);
    setError(null);
    getContactMessages(contactId)
      .then((m) => alive && setMessages(m))
      .catch((err: unknown) => alive && setError(err instanceof Error ? err.message : 'Could not load messages.'));
    return () => {
      alive = false;
    };
  }, [contactId]);

  return (
    <section className="bc-cmsgs" aria-label="Recent messages">
      <h3 className="bc-legend">
        Recent messages {messages && messages.length > 0 && <span className="bc-count">{messages.length}</span>}
      </h3>
      {error ? (
        <p className="bc-formerr">{error}</p>
      ) : messages === null ? (
        <div className="skeleton bc-skel bc-skel--tall" />
      ) : messages.length === 0 ? (
        <p className="ui-group__foot">No messages yet. Texts from any of their handles show up here.</p>
      ) : (
        <div className="bc-thread">
          {messages.map((m) => (
            <MessageRow key={m.id} m={m} />
          ))}
        </div>
      )}
    </section>
  );
}

// --- Inbox (unlinked handles) -----------------------------------------------

function Inbox({
  contacts,
  onAdd,
}: {
  contacts: Contact[] | null;
  onAdd: (handle: string, name?: string | null) => void;
}): JSX.Element {
  const [messages, setMessages] = useState<InboxMessage[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    getInboxMessages()
      .then(setMessages)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Could not load the inbox.'));
  }, []);
  useEffect(load, [load]);

  // A handle added as a contact this session drops out without a refetch.
  const known = useMemo(() => new Set((contacts ?? []).flatMap((c) => c.handles.map((h) => h.value))), [contacts]);
  const pending = useMemo(() => (messages ?? []).filter((m) => !known.has(m.handle)), [messages, known]);

  return (
    <section aria-label="Unknown senders">
      {error ? (
        <p className="bc-banner" role="alert">
          {error}
        </p>
      ) : messages === null ? (
        <ul className="ui-group" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <li key={i} className="ui-swipe">
              <div className="ui-row">
                <span className="skeleton bc-skel" />
              </div>
            </li>
          ))}
        </ul>
      ) : pending.length === 0 ? (
        <EmptyState icon="check" title="All senders known" hint="Every recent message came from someone you already know." />
      ) : (
        <Group
          action={{ label: 'Refresh', onClick: load }}
          header={<>Unknown senders <span className="bc-count">{pending.length}</span></>}
          footer="Add the sender and the dispatcher starts recognising them."
        >
          {pending.map((m) => (
            <Row
              key={m.id}
              leading={m.contactName ? <KindAvatar name={m.contactName} kind="person" /> : <Avatar name="?" icon="users" />}
              title={m.contactName || displayHandle(m.handle)}
              sub={
                <>
                  {m.contactName && <span className="bc-sub-line">{displayHandle(m.handle)}</span>}
                  <span className="bc-clamp">{m.text || (m.attachments.length ? m.attachments.map(attachmentLabel).join(', ') : '')}</span>
                  <span className="bc-sub-line bc-detail-xs">
                    {m.channel} · {fmtMessageTime(m.sentAt)}
                  </span>
                </>
              }
              trailing={
                <Button size="sm" variant="secondary" onClick={() => onAdd(m.handle, m.contactName)}>
                  Add
                </Button>
              }
            />
          ))}
        </Group>
      )}
    </section>
  );
}
